import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTable, MatTableModule } from '@angular/material/table';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { CpfCnpjService } from 'src/app/core/service/cpf-cnpj.service';
import { DialogService } from 'src/app/core/service/dialog.service';
import { NoSpecialCharsDirective } from '../../directive/no-special-chars.directive';
import { StringUtil } from '../../util/string-util';
import { CustomValidators } from '../../validators/custom-validators';
import { Titular } from 'src/app/core/model/titular';
import { Globals } from '../../helpers/globals';
import { TipoOrdem } from 'src/app/core/model/enum/tipo-ordem.enum';
import { OrdemTextUtil } from '../../util/ordem-text-util';

@Component({
  selector: 'app-lista-titulares',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    FormsModule,
    MatIconModule,
    MatInputModule,
    MatTableModule,
    MatMenuModule,
    MatButtonModule,
    NgxMaskDirective,
    NgxMaskPipe,
    NoSpecialCharsDirective
  ],
  templateUrl: './lista-titulares.component.html',
  styleUrl: './lista-titulares.component.css',
  providers: [provideNgxMask()],
})
export class ListaTitularesComponent {

  @Input() tipoOrdem: TipoOrdem;
  @Input() titulares: Titular[] = [];
  @Output() titularesChange = new EventEmitter<Titular[]>();

  form: UntypedFormGroup;

  @ViewChild(MatTable) table: MatTable<Titular>;
  displayedColumns: string[] = ["nome", "documento", "action"];

  constructor(
    private cpfCnpjService: CpfCnpjService,
    private fb: FormBuilder,
    private globals: Globals,
    private dialogService: DialogService,
  ){
    this.form = this.fb.group({
      nome: ["", [Validators.required, CustomValidators.validatorOnlyNumber]],
      documento: ["", Validators.required],
    })
  }

  public addTitular(nome: string, documento: string) {
    let titular = this.buildTitular(nome, documento);

    if (this.isDocumentoValido(documento)) {
      const docExistente = this.titulares.filter(titular => titular.documento == documento);
      if (docExistente.length > 0) {
        this.globals.showSnackBar(`${this.getTituloByTipoOrdem()} já cadastrado!`);
        return;
      }
      this.titulares.push(titular);
      this.nome.reset();
      this.documento.reset();
      if (this.table != undefined)
        this.table.renderRows();
      if (this.titulares.length === 1) {
        this.showInformativoTitularesDialog();
      }
    } else{
      this.globals.showSnackBar("Número do documento inválido!");
      return;
    }
    this.titularesChange.emit(this.titulares);
  }

  public removerTitular(titular) {
    let index = this.titulares.indexOf(titular);
    this.titulares.splice(index, 1);
    this.titularesChange.emit(this.titulares);
    this.table.renderRows();
  }

  public isPessoaFisica(nroDocumento: string) {
    return nroDocumento.length == 11;
  }

  public getTituloByTipoOrdem(isPlural = false): string{
    return OrdemTextUtil.getTituloByTipoOrdem(this.tipoOrdem , isPlural);
  }

  public disableAddTitular(): boolean {
    return this.nome.hasError('required') || this.nome.hasError('valueOnlyNumberError')
      || this.documento.hasError('required');
  }

  public showInformativoTitularesDialog() {
    this.dialogService.showAlert([this.getMensagemInformativa()]).subscribe();
  }

  public getMensagemErroApenasNumeros(): string {
    const conjuncao = this.tipoOrdem === TipoOrdem.OUTROS ? 'da' : 'do';
    return `Nome ${conjuncao} ${this.getTituloByTipoOrdem().toLowerCase()} não pode conter apenas números`;
  }

  public getMensagemDeErroVazio(): string {
    const conjuncao = this.tipoOrdem === TipoOrdem.OUTROS ? 'uma' : 'um';
    const variante = this.tipoOrdem === TipoOrdem.OUTROS ? 'adicionada!' : 'adicionado!';
    return `Ao menos ${conjuncao} ${this.getTituloByTipoOrdem().toLowerCase()} deve ser ${variante}`;
  }

  public getMensagemInformativa(): string {
    return `Se houver mais ${this.getTituloByTipoOrdem(true).toLowerCase()}, adicionar nesse momento!`;
  }

  private isDocumentoValido(documento: string) {
    return this.cpfCnpjService.validateCpfCnpj(documento);
  }

  private buildTitular(nome: string, documento: string): Titular {
    return {
      nome: nome.trim(),
      documento: StringUtil.removeNonNumber(documento)
    };
  }

  get nome() {
    return this.form.get("nome");
  }

  get documento() {
    return this.form.get("documento");
  }

}
